/**
 * Dynamic block OBJECTS bridge — DXF <-> DrawingModel (DYN-1.2 + 1.3 + 1.4).
 *
 * This module is the public surface. The actual parser (DYN-1.2) and
 * writer (DYN-1.3) live in sibling files; this module re-exports them
 * and provides the bridge (DYN-1.4) that glues the drawing-model maps
 * (`blocks`, `blockRecords`) to the raw OBJECTS section.
 *
 * ── Module layout ──
 *
 *   dxfBridge.dynamic.ts              ← THIS FILE (bridge + barrel)
 *   dxfBridge.dynamic.parser.ts       ← DYN-1.2 — DXF → metadata
 *   dxfBridge.dynamic.writer.ts       ← DYN-1.3 — metadata → DXF
 *   dxfBridge.dynamic.shared.ts       ← group-lookup / push helpers
 *
 * ── Group codes (reverse-engineered, common patterns) ──
 *
 * All parameter / action classes share:
 *   0   class name (ACDBBLOCKLINEARPARAMETER, …)
 *   5   handle
 *   330 ownerHandle (BLOCK_RECORD handle for parameters, parent
 *       parameter for actions — but AutoCAD sometimes uses 340 too)
 *   100 subclass marker (AcDbObject / AcDbBlock* / AcDbEvalExpr)
 *   1   parameter / action name (label)
 *
 * Linear parameter:
 *   10/20   basePoint            11/21 secondPoint
 *   140     defaultDistance      141   currentDistance
 *   1071    value-set mode       142   valueSet.increment
 *   143     valueSet.min         144   valueSet.max
 *   145     listValues[]
 *
 * Flip parameter:
 *   10/20   axisStart            11/21 axisEnd
 *   280     defaultValue (0/1)   281   currentValue (0/1)
 *   300     flipLabel            301   baseLabel
 *
 * Visibility parameter:
 *   10/20   position
 *   91      state count          340 default state handle
 *   341     current state handle
 *
 * Visibility state (child object):
 *   2       state name           330  owner parameter handle
 *   91      invisible entity count
 *   340..   invisible entity handles (one entry per handle)
 *
 * BasePoint parameter:
 *   10/20   position
 *
 * Move action:
 *   330     owner (parent parameter)
 *   340     (optional second parameter reference)
 *   440     distance multiplier  441 angle offset (degrees)
 *   91      selection count
 *   340..   selection entity handles
 *
 * Stretch action:
 *   same as Move +
 *   10/20   crossing frame corner 1
 *   11/21   crossing frame corner 2
 *   50      stretch direction (degrees)
 *
 * Flip action:
 *   330     owner parameter
 *   91      selection count
 *   340..   selection entity handles
 *
 * ── Approach to unknown codes ──
 *
 * Every object retains its full `rawGroups` list. Tier-1 fields are
 * decoded into typed members of `DynamicBlockMetadata`. Any group whose
 * code/class is not recognised still round-trips byte-for-byte through
 * `rawGroups`. When a parameter or action class lies outside the Tier-1
 * set, the bridge preserves the raw object under
 * `meta.futureExtensions.rawObjects` so the writer can re-emit it on
 * export.
 */
import type {
  DxfDynamicBlockObject,
  DxfObjectsSection,
  DxfTablesSection,
} from "@dxf-ai-editor/dxf-io";

import type { BlockDefinition, BlockRecord } from "./model";

export { buildDynamicBlockMetadata } from "./dxfBridge.dynamic.parser";
export { emitDynamicBlockObjects } from "./dxfBridge.dynamic.writer";
export {
  detectDynamicBlocks,
  detectDynamicBlocksFromSections,
} from "./dxfBridge.dynamic.detect";
export {
  extractDynamicBlockParams,
  extractDynamicBlockParamsFromSections,
  attachInsertCustomProperties,
  type ParamDef,
} from "./dxfBridge.dynamic.params";
export { buildVisibilityMetadataFromRawGroups } from "./dxfBridge.dynamic.visibility";

// NOTE: per CLAUDE.md "split file imports" rule, the bridge functions
// below import their own copy of the parser / writer entry points — a
// re-export does NOT make the symbol available for local use.
import { buildDynamicBlockMetadata } from "./dxfBridge.dynamic.parser";
import { emitDynamicBlockObjects } from "./dxfBridge.dynamic.writer";
import { buildVisibilityMetadataFromRawGroups } from "./dxfBridge.dynamic.visibility";
import { buildBlockToEvalGraph } from "./dxfBridge.dynamic.params";
import { collectAssocVariables } from "@dxf-ai-editor/dxf-io";
import { collectHandlesDeep } from "./dxfBridge.annotativeExport";
import type { DynamicParameter } from "./dynamicBlock";
import {
  collectAssocConstraintRecords,
  indexChildToGroup,
  indexGeomDepsByGroup,
  resolveAssociatedEntities,
  type AssocGeomDependency,
} from "./dxfBridge.dynamic.parser.assocdeps";
import { extractValueDepHandle } from "./dxfBridge.dynamic.parser.constraints";

// ═══════════════════════════════════════════════════════════════════════════
// Constraint-parameter associated-entity enrichment helper
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Walk a DynamicBlockMetadata's parameters and, for every dimensional
 * constraint parameter that has a resolvable value-dep → group → geom-deps
 * chain in the pre-scanned indices, populate its `associatedEntities[]`
 * field. Returns a NEW metadata instance — input is not mutated.
 *
 * Parameters without a value-dep handle (legacy fixtures / synthesised
 * objects / round-trip writer output) or whose value-dep does not appear
 * in any constraint group receive an empty `associatedEntities[]`, which
 * makes downstream collectors fall back to coordinate matching.
 */
function enrichConstraintParamsWithAssocEntities(
  meta: import("./dynamicBlock").DynamicBlockMetadata,
  paramHandleToValueDepHandle: ReadonlyMap<string, string>,
  childToGroup: ReadonlyMap<string, string>,
  geomDepsByGroup: ReadonlyMap<string, readonly AssocGeomDependency[]>,
  groupsByHandle: ReadonlyMap<
    string,
    import("./dxfBridge.dynamic.parser.assocdeps").Assoc2dConstraintGroup
  >,
  depChains: ReadonlyMap<string, readonly string[]>,
): import("./dynamicBlock").DynamicBlockMetadata {
  let anyChanged = false;
  const nextParams: DynamicParameter[] = meta.parameters.map((p) => {
    switch (p.kind) {
      case "constraint-horizontal":
      case "constraint-vertical":
      case "constraint-aligned":
      case "constraint-angular":
      case "constraint-radial":
      case "constraint-diameter": {
        // Param ids are the parameter's DXF handle (set by parsers).
        const valueDepHandle = paramHandleToValueDepHandle.get(p.id) ?? "";
        if (valueDepHandle.length === 0) return p;
        const refs = resolveAssociatedEntities(
          valueDepHandle,
          childToGroup,
          geomDepsByGroup,
          groupsByHandle,
          depChains,
        );
        if (refs.length === 0) return p;
        anyChanged = true;
        return { ...p, associatedEntities: refs.map((r) => ({ ...r })) };
      }
      default:
        return p;
    }
  });
  if (!anyChanged) return meta;
  return { ...meta, parameters: nextParams };
}

// ═══════════════════════════════════════════════════════════════════════════
// Bridge (DYN-1.4)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Walk the BLOCK_RECORD / BlockDefinition pair and attach
 * `dynamicMetadata` to every block whose BLOCK_RECORD handle appears as
 * an owner in the OBJECTS dynamic-block cluster. Returns a new map —
 * input `blocks` is left unchanged.
 *
 * Two-pass strategy (Variant D Phase 1):
 *   1. Prefixed-parser pass — walk `dynamicBlockObjects[]` (ACDB*)
 *      and attach metadata for ezdxf / round-tripped fixtures. This
 *      is the AUTHORITATIVE source when available.
 *   2. Un-prefixed visibility pass — for blocks still without
 *      metadata after pass 1, walk `rawObjectGroups[]` for
 *      `BLOCKVISIBILITYPARAMETER` / `BLOCKVISIBILITYSTATE` records
 *      and synthesize typed metadata. Covers real AutoCAD 2018+
 *      output (АБИ1.dxf dominant encoding).
 *
 * The optional `tablesSection` is required by the Phase 1 visibility
 * builder (it needs the BLOCK_RECORD → extension-dictionary link to
 * resolve each block's ACAD_EVALUATION_GRAPH handle). When omitted,
 * only pass 1 runs — callers that don't supply tables get prefixed-
 * parser behaviour unchanged.
 */
export function attachDynamicBlockMetadata(
  blocks: Record<string, BlockDefinition>,
  blockRecords: Record<string, BlockRecord>,
  objectsSection: DxfObjectsSection | null,
  tablesSection?: DxfTablesSection | null,
): Record<string, BlockDefinition> {
  if (!objectsSection) return blocks;
  const dynamicObjects = objectsSection.dynamicBlockObjects;
  const out: Record<string, BlockDefinition> = { ...blocks };

  // DYN-4 Phase 3 Fix 6 — pre-scan ACDBASSOCVARIABLE records once per
  // import so the BLOCKUSERPARAMETER parser (invoked inside
  // buildDynamicBlockMetadata) can resolve variable name / value / type
  // in O(1) rather than re-walking the OBJECTS groups for every block.
  const assocVariables = collectAssocVariables(objectsSection.rawObjectGroups);

  // Pre-scan ACDBASSOC2DCONSTRAINTGROUP + ACDBASSOCGEOMDEPENDENCY once
  // per import so every constraint parameter can resolve its
  // associatedEntities[] without re-walking the OBJECTS groups per
  // block. The indices let us map:
  //   param.valueDepHandle → constraint group → geom-deps → entity handles
  // in O(1). When these records are absent (all non-AutoCAD 2018+
  // fixtures), the indices stay empty and parameters receive empty
  // `associatedEntities[]` — downstream collectors fall back to the
  // coordinate-match path.
  const assocRecords = collectAssocConstraintRecords(
    objectsSection.rawObjectGroups,
  );
  const geomDepsByGroup = indexGeomDepsByGroup(assocRecords.geomDeps);
  const childToGroup = indexChildToGroup(assocRecords.groups);

  // Pre-scan value-dep handles for every dynamic-block-object classified
  // as a constraint parameter. Avoids re-walking raw groups inside the
  // enrichment helper; the map is keyed by the parameter handle.
  const paramHandleToValueDepHandle = new Map<string, string>();
  for (const obj of dynamicObjects) {
    const type = obj.type.toUpperCase();
    if (
      type.includes("CONSTRAINTPARAMETER") ||
      type.includes("DIAMETRICCONSTRAINTPARAMETER")
    ) {
      const handle = extractValueDepHandle(obj.rawGroups);
      if (handle.length > 0) paramHandleToValueDepHandle.set(obj.handle, handle);
    }
  }

  // DYN-4 Phase 6 — resolve block name → ACAD_EVALUATION_GRAPH handle so
  // pass 1 can BFS from the eval_graph when neither the BLOCK header
  // handle nor the BLOCK_RECORD handle owns any dynamic objects directly.
  // Real AutoCAD 2018+ files chain from eval_graph → (parameters,
  // BLOCKPROPERTIESTABLE → driven user/constraint params, grips, …),
  // so without this third candidate the ibeam / constraint-based blocks
  // receive ZERO typed parameters from pass 1. See diagnostic trace in
  // `dxfBridge.dynamic.phase6.abi1.test.ts`.
  const blockNameToEvalGraph =
    tablesSection !== undefined && tablesSection !== null
      ? buildBlockToEvalGraph(tablesSection, objectsSection)
      : new Map<string, string>();

  // ── Pass 1: prefixed ACDB* → typed metadata (authoritative) ──
  if (dynamicObjects.length > 0) {
    for (const [name, block] of Object.entries(blocks)) {
      // AutoCAD uses a separate BLOCK_RECORD handle (group 5 of the
      // BLOCK_RECORD entry); AutoCAD DXF emitted from authoring tools
      // sometimes points the ACDBBLOCKREPRESENTATION.ownerHandle at the
      // BLOCK handle (the BLOCKS-section handle) rather than the
      // BLOCK_RECORD handle. We try both so the bridge survives either
      // convention — whichever matches first wins.
      //
      // DYN-4 Phase 6 — AutoCAD 2018+ files chain eval_graph → BPT →
      // user/constraint params (see diagnostic `dxfBridge.dynamic.phase6
      // .diagnostic.test.ts`), so the BFS must start from the
      // ACAD_EVALUATION_GRAPH handle rather than the BLOCK or
      // BLOCK_RECORD handle. We prefer eval_graph when available and
      // fall back to handle / ownerHandle for legacy fixtures.
      //
      // We record which candidate origin produced the successful match so
      // downstream steps (orphan-passthrough in `dxfBridge.import.ts`,
      // `exportDynamicBlockObjects`) can preserve verbatim round-trip for
      // eval_graph-only blocks. Legacy blocks whose BLOCK header /
      // BLOCK_RECORD handle matched continue to use the synthesizer
      // path so existing round-trip tests stay green.
      const candidates: { handle: string; kind: "eval" | "legacy" }[] = [];
      const evalHandle = blockNameToEvalGraph.get(name);
      if (evalHandle) candidates.push({ handle: evalHandle, kind: "eval" });
      if (block.handle) candidates.push({ handle: block.handle, kind: "legacy" });
      // BlockRecord currently lacks a `handle` field on the drawing-model
      // side; we skip the BLOCK_RECORD handle here and rely on
      // BlockDefinition.handle + BlockDefinition.ownerHandle + the
      // futureExtensions preservation to cover the gap.
      if (block.ownerHandle) candidates.push({ handle: block.ownerHandle, kind: "legacy" });

      for (const { handle, kind } of candidates) {
        const metaRaw = buildDynamicBlockMetadata(
          handle,
          dynamicObjects,
          assocVariables,
        );
        const meta = metaRaw
          ? enrichConstraintParamsWithAssocEntities(
              metaRaw,
              paramHandleToValueDepHandle,
              childToGroup,
              geomDepsByGroup,
              assocRecords.groups,
              assocRecords.depChains,
            )
          : metaRaw;
        // Attach metadata whenever the parser produced anything we need
        // to round-trip — parameters, actions, OR preserved raw objects
        // under futureExtensions. The third condition matters for
        // AutoCAD 2018+ files whose un-prefixed BLOCK* classes
        // (DYN-4 Fix 1) decode to typed parameters AND/OR land in
        // futureExtensions.rawObjects; without it, blocks whose
        // parameters live only in futureExtensions would be dropped
        // on export (losing ~1.5 MB of the АБИ1.dxf fixture).
        const preservedRaw =
          (meta?.futureExtensions.rawObjects as
            | DxfDynamicBlockObject[]
            | undefined) ?? [];
        if (
          meta &&
          (meta.parameters.length > 0 ||
            meta.actions.length > 0 ||
            preservedRaw.length > 0)
        ) {
          // DYN-4 Phase 6 — when pass 1 succeeded ONLY via the eval_graph
          // handle (the legacy BLOCK / BLOCK_RECORD candidates found
          // nothing), flag the metadata so the export path knows to skip
          // typed synthesis and rely on the orphan-passthrough copy of
          // the raw records. This preserves byte-perfect round-trip for
          // constraint-based blocks (АБИ1.dxf) while still exposing typed
          // parameters to the Inspector / DYNSET / editorInspect().
          const attachedMeta =
            kind === "eval"
              ? {
                  ...meta,
                  futureExtensions: {
                    ...meta.futureExtensions,
                    // In-memory marker only — schema ignores unknown keys.
                    // `dxfBridge.import.ts` reads this to skip claimedHandles;
                    // `exportDynamicBlockObjects` reads it to skip synth emit.
                    evalGraphSourced: true,
                  },
                }
              : meta;
          out[name] = { ...block, dynamicMetadata: attachedMeta };
          break;
        }
      }
    }
  }

  // ── Pass 2: un-prefixed BLOCKVISIBILITY* → typed metadata ──
  // Pass 1 is authoritative for parameters it CLAIMED (linear/rotation/…,
  // and in DYN-4 also user/constraint/BPT). Visibility detection still
  // lives in pass 2 (constraint-based blocks often carry only raw
  // visibility groups in the legacy TABLES/OBJECTS chain). Merge — don't
  // skip — so the ibeam case where pass 1 attached rawObjects-only
  // metadata still picks up its 3-state visibility param.
  if (tablesSection !== undefined && tablesSection !== null) {
    const visibilityMeta = buildVisibilityMetadataFromRawGroups(
      tablesSection,
      objectsSection,
      out,
    );
    for (const [name, vis] of visibilityMeta.entries()) {
      const block = out[name];
      if (!block) continue;
      const existing = block.dynamicMetadata;
      if (!existing) {
        out[name] = { ...block, dynamicMetadata: vis };
        continue;
      }
      // Pass 1 already put something there — merge visibility params
      // unless pass 1 already resolved a visibility parameter (authoritative).
      const hasVisibilityFromPass1 = existing.parameters.some(
        (p) => p.kind === "visibility",
      );
      if (hasVisibilityFromPass1) continue;
      const visParams = vis.parameters.filter((p) => p.kind === "visibility");
      if (visParams.length === 0) continue;
      out[name] = {
        ...block,
        dynamicMetadata: {
          ...existing,
          parameters: [...existing.parameters, ...visParams],
          // Preserve pass 2's current state selector if it set one.
          currentVisibilityState:
            existing.currentVisibilityState ?? vis.currentVisibilityState,
        },
      };
    }
  }

  // blockRecords is currently unused — it is accepted to preserve a
  // forward-compatible signature once BlockRecord gains a `handle`
  // field (which would let us match via BLOCK_RECORD group 5 rather
  // than the BLOCK header group 5).
  void blockRecords;
  return out;
}

/**
 * Flatten every dynamic block's metadata into DXF OBJECTS records ready
 * for the writer. Blocks without `dynamicMetadata` contribute nothing.
 *
 * DYN-4 Phase 6 — blocks flagged with `futureExtensions.evalGraphSourced`
 * are skipped here: their raw records survive via `rawObjectGroups`
 * passthrough (populated by `dxfBridge.import.ts`). Emitting both paths
 * would double-write the same records and break
 * `dxfBridge.fidelity.test.ts` (АБИ1.dxf ~9 MB round-trip floor).
 */
export function exportDynamicBlockObjects(
  blocks: Record<string, BlockDefinition> | undefined,
  sharedNextHandle?: () => string,
): DxfDynamicBlockObject[] {
  if (!blocks) return [];
  const out: DxfDynamicBlockObject[] = [];
  // SL-6 (R2018 `cross-block-handle-collision` P1) — thread ONE monotonic
  // handle minter across EVERY block so synthesized group-5 handles are
  // unique document-wide. The top-level export orchestrator passes a minter
  // seeded ABOVE the document's handle-max (`makeHandleMinter`); when called
  // standalone (no seed) we still share a SINGLE counter across all blocks
  // instead of restarting each block at 0x100000 (which emitted duplicate
  // handles across ≥2 dynamic blocks → strict ezdxf non-unique reject).
  let fallbackCounter = 0x100000;
  const mint =
    sharedNextHandle ??
    ((): string => {
      fallbackCounter += 1;
      return fallbackCounter.toString(16).toUpperCase();
    });
  for (const block of Object.values(blocks)) {
    if (!block.dynamicMetadata) continue;
    if (block.dynamicMetadata.futureExtensions?.evalGraphSourced === true) {
      // Records live verbatim in rawObjectGroups — do not synthesize.
      continue;
    }
    // Use BlockDefinition.handle as the BLOCK_RECORD-handle proxy — see
    // the note in attachDynamicBlockMetadata above. SL-6 `nonhex-owner-fallback`
    // P2 — a handle-less block previously fell back to the NON-hex literal
    // `*dyn-<name>` (invalid in a group-330 owner slot; the name may be
    // Cyrillic). Mint a valid HEX handle from the same shared minter instead so
    // every synthesized object's 330 owner is a well-formed handle.
    const blockRecordHandle = block.handle || mint();
    out.push(
      ...emitDynamicBlockObjects(blockRecordHandle, block.dynamicMetadata, mint),
    );
  }
  return out;
}

/**
 * SL-6 (R2018 `cross-block-handle-collision` P1, retained-handle arm) —
 * collect (lowercased) EVERY group-5 handle that {@link exportDynamicBlockObjects}
 * will emit VERBATIM from an imported / non-eval dynamic block, so the shared
 * minter's seed population accounts for them.
 *
 * `exportDynamicBlockObjects` mints synthetic handles for programmatically-built
 * objects, but for objects parsed from a source DXF it re-emits the RETAINED
 * source handle verbatim (`p.id || mint()`, `a.id || mint()`, `state.id`,
 * `c.id`, `table.id`/`table.handle`, `grip.id`). Those retained ids are NOT in
 * `preDynamicHandles` (that set is collected from the assembled sections BEFORE
 * the dynamic objects exist), so a minter seeded only from `preDynamicHandles`
 * could mint a NEW handle that COLLIDES with a retained one → duplicate group-5
 * (the very defect SL-6 eliminates, for the retained-handle case). Folding this
 * set into the seed guarantees the monotonic minter never re-issues a value the
 * writer also emits verbatim.
 *
 * Two harvest paths, mirroring the two ways the writer carries a handle:
 *   1. {@link collectHandlesDeep} (the ONE deep handle scanner — no second
 *      walker) catches every `handle` / `*Handle`-keyed field and every verbatim
 *      `{code: 5 | 105 | 1005}` raw-group leaf: grip `ownerHandle` /
 *      `parameterHandle` / fidelity-path `rawGroups`, `blockPropertiesTable.handle`,
 *      and `futureExtensions.rawObjects[].handle` + their `rawGroups`.
 *   2. An explicit `.id` sweep for the retained handles the writer emits from the
 *      `.id` field — which `collectHandlesDeep` structurally cannot reach (`id`
 *      is not a `*Handle` key): parameters (+ their visibility states), actions,
 *      geometric constraints, lookup tables, grips, and the properties table.
 *
 * The block-skip logic MIRRORS `exportDynamicBlockObjects` exactly (static +
 * eval-graph-sourced blocks emit nothing here — their records survive via
 * `rawObjectGroups` passthrough, already counted in `preDynamicHandles`).
 */
export function collectRetainedDynamicBlockHandles(
  blocks: Record<string, BlockDefinition> | undefined,
  out: Set<string>,
): void {
  if (!blocks) return;
  const add = (handle: string | undefined): void => {
    if (handle && handle.length > 0) out.add(handle.toLowerCase());
  };
  for (const block of Object.values(blocks)) {
    const meta = block.dynamicMetadata;
    if (!meta) continue;
    if (meta.futureExtensions?.evalGraphSourced === true) continue;
    // Path 1 — structural handle fields + raw group-5/105/1005 leaves.
    collectHandlesDeep(meta, out);
    // Path 2 — `.id`-carried retained handles collectHandlesDeep cannot see.
    for (const p of meta.parameters) {
      add(p.id);
      if (p.kind === "visibility") {
        for (const s of p.states) add(s.id);
      }
    }
    for (const a of meta.actions) add(a.id);
    for (const c of meta.geometricConstraints ?? []) add(c.id);
    for (const t of meta.lookupTables ?? []) add(t.id);
    for (const g of meta.grips ?? []) add(g.id);
    if (meta.blockPropertiesTable) add(meta.blockPropertiesTable.id);
  }
}
